import React from 'react';
import { RefreshCw, Check, Users, Plus } from 'lucide-react';
import { BatchGiftEntry } from '../../../types/invitation';
import ThemedCard from '../../themed/ThemedCard';
import { GiftTabId } from '../types';

interface BulkEntryTabProps {
  batchEntries: BatchGiftEntry[];
  loadingBatchData: boolean;
  batchProcessing: boolean;
  onLoadBatchData: () => Promise<void>;
  onBatchSubmit: () => Promise<void>;
  onUpdateAmount: (rsvpId: string, amount: number) => void;
  onUpdateMessage: (rsvpId: string, message: string) => void;
  onSetActiveTab: (tab: GiftTabId) => void;
}

const BatchEntryCard: React.FC<{
  entry: BatchGiftEntry;
  colorScheme: 'pink' | 'gold';
  onUpdateAmount: (rsvpId: string, amount: number) => void;
  onUpdateMessage: (rsvpId: string, message: string) => void;
}> = ({ entry, colorScheme, onUpdateAmount, onUpdateMessage }) => {
  const borderColor = colorScheme === 'pink' ? 'border-wedding-pink/20' : 'border-wedding-gold/20';
  const inputBorderColor = colorScheme === 'pink' ? 'border-wedding-pink/30' : 'border-wedding-gold/30';
  const inputFocusColor = colorScheme === 'pink'
    ? 'focus:ring-wedding-pink focus:border-wedding-pink'
    : 'focus:ring-wedding-gold focus:border-wedding-gold';
  const badgeBg = colorScheme === 'pink' ? 'bg-wedding-pink/20 text-wedding-pink' : 'bg-wedding-gold/20 text-wedding-gold';

  return (
    <div className={`border ${borderColor} rounded-lg p-4`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <h4 className="font-medium text-wedding-text-title">{entry.guestName}</h4>
          <p className="text-sm text-wedding-text-body">{entry.guestEmail}</p>
          <div className="flex items-center space-x-2 mt-1">
            <span className={`text-xs px-2 py-1 rounded-full ${
              entry.attendingStatus === 'yes'
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {entry.attendingStatus === 'yes' ? '出席' : '不出席'}
            </span>
            <span className="text-xs text-wedding-text-body">
              {entry.guestCount} 人
            </span>
          </div>
        </div>
        {entry.hasExistingRecord && (
          <span className={`text-xs ${badgeBg} px-2 py-1 rounded-full`}>
            已登記
          </span>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-wedding-text-title mb-1">
            紅包金額 (TWD)
          </label>
          <input
            type="number"
            min="0"
            step="100"
            value={entry.amount || ''}
            onChange={(e) => onUpdateAmount(entry.rsvpId, Number(e.target.value))}
            className={`w-full px-3 py-2 border ${inputBorderColor} rounded-lg ${inputFocusColor} focus:ring-2`}
            placeholder="輸入金額"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-wedding-text-title mb-1">
            留言
          </label>
          <input
            type="text"
            value={entry.message || ''}
            onChange={(e) => onUpdateMessage(entry.rsvpId, e.target.value)}
            className={`w-full px-3 py-2 border ${inputBorderColor} rounded-lg ${inputFocusColor} focus:ring-2`}
            placeholder="祝福留言（選填）"
          />
        </div>
      </div>
    </div>
  );
};

export const BulkEntryTab: React.FC<BulkEntryTabProps> = ({
  batchEntries,
  loadingBatchData,
  batchProcessing,
  onLoadBatchData,
  onBatchSubmit,
  onUpdateAmount,
  onUpdateMessage,
  onSetActiveTab,
}) => {
  const groomEntries = batchEntries.filter(entry => entry.guestSide === 'groom');
  const brideEntries = batchEntries.filter(entry => entry.guestSide === 'bride');
  const validCount = batchEntries.filter(e => e.amount > 0).length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-wedding-text-title">批量登記紅包</h2>
          <p className="text-sm text-wedding-text-body mt-1">
            從 RSVP 賓客名單快速登記紅包，按親友分類顯示
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={onLoadBatchData}
            disabled={loadingBatchData}
            className="flex items-center px-4 py-2 bg-wedding-pink/10 text-wedding-pink rounded-lg hover:bg-wedding-pink/20 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loadingBatchData ? 'animate-spin' : ''}`} />
            重新載入
          </button>
          <button
            onClick={onBatchSubmit}
            disabled={batchProcessing || validCount === 0}
            className="flex items-center px-6 py-2 bg-wedding-pink text-white rounded-lg hover:bg-wedding-pink/90 transition-colors disabled:opacity-50"
          >
            {batchProcessing ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            批量提交 ({validCount})
          </button>
        </div>
      </div>

      {loadingBatchData ? (
        <div className="flex justify-center items-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-wedding-pink" />
          <span className="ml-2 text-wedding-text-body">載入 RSVP 資料中...</span>
        </div>
      ) : (
        <>
          {groomEntries.length > 0 && (
            <ThemedCard className="p-6">
              <h3 className="text-lg font-semibold text-wedding-text-title mb-4 flex items-center">
                <div className="w-4 h-4 bg-wedding-pink rounded-full mr-2"></div>
                男方親友 ({groomEntries.length} 位)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groomEntries.map((entry) => (
                  <BatchEntryCard
                    key={entry.rsvpId}
                    entry={entry}
                    colorScheme="pink"
                    onUpdateAmount={onUpdateAmount}
                    onUpdateMessage={onUpdateMessage}
                  />
                ))}
              </div>
            </ThemedCard>
          )}

          {brideEntries.length > 0 && (
            <ThemedCard className="p-6">
              <h3 className="text-lg font-semibold text-wedding-text-title mb-4 flex items-center">
                <div className="w-4 h-4 bg-wedding-gold rounded-full mr-2"></div>
                女方親友 ({brideEntries.length} 位)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {brideEntries.map((entry) => (
                  <BatchEntryCard
                    key={entry.rsvpId}
                    entry={entry}
                    colorScheme="gold"
                    onUpdateAmount={onUpdateAmount}
                    onUpdateMessage={onUpdateMessage}
                  />
                ))}
              </div>
            </ThemedCard>
          )}

          {batchEntries.length === 0 && !loadingBatchData && (
            <ThemedCard className="p-8 text-center">
              <Users className="h-12 w-12 text-wedding-text-body mx-auto mb-4" />
              <h3 className="text-lg font-medium text-wedding-text-title mb-2">尚無 RSVP 資料</h3>
              <p className="text-wedding-text-body mb-4">
                需要先有賓客透過邀請函頁面回覆 RSVP，才能使用批量登記功能。
              </p>
              <button
                onClick={() => onSetActiveTab('add')}
                className="inline-flex items-center px-4 py-2 bg-wedding-pink text-white rounded-lg hover:bg-wedding-pink/90 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                新增紅包記錄
              </button>
            </ThemedCard>
          )}
        </>
      )}
    </div>
  );
};
